export function truncTo2Decimals(value: number): number {
  return Math.trunc(value * 100) / 100;
}

export function truncTo6Decimals(value: number): number {
  return Math.trunc(value * 1000000) / 1000000;
}

export function roundTo2Decimals(value: number): number {
  return Math.round(value * 100) / 100;
}

export interface SingleForeignPaymentForex {
  userExchangeRate: number;
  amountForZoho: number;
  realisationINR: number;
  bankChargesFC: number;
  bankChargesINR: number;
  exchangeRateForZoho: number;
}

/**
 * Zoho reconciles foreign customer/vendor payments with:
 *   trunc2(amount × rate) − trunc2(bank_charges × rate) = transaction INR
 *
 * Zoho also requires:
 *   exchange_rate = trunc6(transaction / (amount − bank_charges))
 */
export function zohoReconciledAmountINR(
  appliedAmountFC: number,
  bankChargesFC: number,
  exchangeRate: number,
): number {
  return (
    truncTo2Decimals(appliedAmountFC * exchangeRate) -
    truncTo2Decimals(bankChargesFC * exchangeRate)
  );
}

interface ZohoForexPayload {
  exchangeRate: number;
  bankChargesFC: number;
}

function deriveBankChargesForDisplay(
  appliedAmountFC: number,
  exchangeRate: number,
  transactionAmountINR: number,
): {
  realisationINR: number;
  bankChargesINR: number;
  bankChargesFC: number;
} {
  const rate = truncTo6Decimals(exchangeRate);
  const realisationINR = appliedAmountFC * rate;
  const bankChargesINR = roundTo2Decimals(
    Math.abs(realisationINR - transactionAmountINR),
  );
  const bankChargesFC =
    bankChargesINR > 0.009 ? roundTo2Decimals(bankChargesINR / rate) : 0;

  return { realisationINR, bankChargesINR, bankChargesFC };
}

function zohoRateFromBankCharges(
  appliedAmountFC: number,
  bankChargesFC: number,
  transactionAmountINR: number,
): number {
  const netAmountFC = appliedAmountFC - bankChargesFC;
  if (netAmountFC <= 0) {
    return 0;
  }
  return truncTo6Decimals(transactionAmountINR / netAmountFC);
}

function isValidZohoPair(
  appliedAmountFC: number,
  exchangeRate: number,
  bankChargesFC: number,
  transactionAmountINR: number,
): boolean {
  if (exchangeRate <= 0 || bankChargesFC < 0) {
    return false;
  }

  const targetINR = roundTo2Decimals(transactionAmountINR);
  const reconciledINR = roundTo2Decimals(
    zohoReconciledAmountINR(appliedAmountFC, bankChargesFC, exchangeRate),
  );
  if (reconciledINR !== targetINR) {
    return false;
  }

  return (
    zohoRateFromBankCharges(
      appliedAmountFC,
      bankChargesFC,
      transactionAmountINR,
    ) === truncTo6Decimals(exchangeRate)
  );
}

// const RATE_SEARCH_MAX_STEPS = 10000;
const RATE_SEARCH_MAX_STEPS = 200;
const RATE_SEARCH_STEP = 0.000001;

/**
 * Keep bank_charges fixed (from reco at user rate). Search exchange_rate
 * outward from the user value until both Zoho amount and rate checks pass.
 */
function findExchangeRateForFixedBankCharges(
  appliedAmountFC: number,
  userExchangeRate: number,
  fixedBankChargesFC: number,
  transactionAmountINR: number,
): number | null {
  const userRate = truncTo6Decimals(userExchangeRate);
  if (userRate <= 0 || fixedBankChargesFC < 0) {
    return null;
  }

  for (let step = 0; step <= RATE_SEARCH_MAX_STEPS; step++) {
    const deltas = step === 0 ? [0] : [step, -step];

    for (const delta of deltas) {
      const candidateRate = truncTo6Decimals(
        userRate + delta * RATE_SEARCH_STEP,
      );
      if (candidateRate <= 0) {
        continue;
      }

      if (
        isValidZohoPair(
          appliedAmountFC,
          candidateRate,
          fixedBankChargesFC,
          transactionAmountINR,
        )
      ) {
        return candidateRate;
      }
    }
  }

  return null;
}

function resolveZohoForexPayload(
  appliedAmountFC: number,
  userExchangeRate: number,
  transactionAmountINR: number,
): ZohoForexPayload {
  const userRate = truncTo6Decimals(userExchangeRate);
  const display = deriveBankChargesForDisplay(
    appliedAmountFC,
    userRate,
    transactionAmountINR,
  );

  // Primary: fixed bank_charges = reco display (2dp), search exchange_rate
  const fixedBankCharges2dp = display.bankChargesFC;
  if (fixedBankCharges2dp > 0) {
    const rate = findExchangeRateForFixedBankCharges(
      appliedAmountFC,
      userExchangeRate,
      fixedBankCharges2dp,
      transactionAmountINR,
    );
    if (rate !== null) {
      return { exchangeRate: rate, bankChargesFC: fixedBankCharges2dp };
    }
  }

  // Fallback: fixed bank_charges at 6dp from user rate, search exchange_rate
  const fixedBankCharges6dp = truncTo6Decimals(
    (appliedAmountFC * userRate - transactionAmountINR) / userRate,
  );
  if (fixedBankCharges6dp > 0) {
    const rate = findExchangeRateForFixedBankCharges(
      appliedAmountFC,
      userExchangeRate,
      fixedBankCharges6dp,
      transactionAmountINR,
    );
    if (rate !== null) {
      return { exchangeRate: rate, bankChargesFC: fixedBankCharges6dp };
    }
  }

  return {
    exchangeRate: userRate,
    bankChargesFC: fixedBankCharges2dp,
  };
}

export function calculateSingleForeignPaymentForex(
  appliedAmountFC: number,
  userExchangeRate: number,
  transactionAmountINR: number,
): SingleForeignPaymentForex {
  const userRate = truncTo6Decimals(userExchangeRate);
  const display = deriveBankChargesForDisplay(
    appliedAmountFC,
    userRate,
    transactionAmountINR,
  );
  const payload = resolveZohoForexPayload(
    appliedAmountFC,
    userExchangeRate,
    transactionAmountINR,
  );

  const bankChargesINR = roundTo2Decimals(
    truncTo2Decimals(payload.bankChargesFC * payload.exchangeRate),
  );

  return {
    userExchangeRate: userRate,
    amountForZoho: truncTo2Decimals(appliedAmountFC),
    realisationINR: roundTo2Decimals(display.realisationINR),
    bankChargesFC: payload.bankChargesFC,
    bankChargesINR,
    exchangeRateForZoho: payload.exchangeRate,
  };
}
